import { useStore } from "react-redux";

import { useTypedDispatch, useTypedSelector } from "@hooks";
import { useLazyFetchAutoPopulateDataQuery } from "@services";
import { RootState } from "@store";
import { handleOfferModeller } from "@store/offers";
import {
  AdvisorBlock,
  IAdvisorNodePayload,
  IAdvisorPackageCardBlock,
  OfferModellerAddedField,
} from "@types";

import { ADVISOR_LINE_ITEM } from "./advisorContract";
import { parseAmount } from "./blocks/tableUtils";

/**
 * Carries the advisor's recommended package into the offer modeller's own fields: set the annual
 * base, pull the company's configured rows via "Fetch Latest Data", then add the discretionary
 * top-up. Nothing else is written — the configured rows are percentages of the base and TGC, so
 * the totals compute themselves from there.
 *
 * Amounts are read back out of the rendered card by label, since the advisor only ships the
 * package as formatted text. If the service ever exposes the raw OfferPackage on the turn's
 * `meta`, read that instead and delete the parsing.
 */

export interface IAdvisorPackageValues {
  annualBase: number;
  discretionaryAllowances: number;
  discretionaryBonus: number;
  annualEquity: number;
  signOnBonus: number;
}

export function findPackageBlock(
  payload: IAdvisorNodePayload
): IAdvisorPackageCardBlock | null {
  return (
    payload.blocks.find(
      (block: AdvisorBlock): block is IAdvisorPackageCardBlock =>
        block.type === "package_card"
    ) ?? null
  );
}

function amountOf(block: IAdvisorPackageCardBlock, label: string): number {
  const item = block.items.find(entry => entry.label === label);
  return (item && parseAmount(item.value)) || 0;
}

/** Null when the card carries no usable base — nothing below it would make sense either. */
export function readPackageValues(
  block: IAdvisorPackageCardBlock
): IAdvisorPackageValues | null {
  const annualBase = amountOf(block, ADVISOR_LINE_ITEM.ANNUAL_BASE);
  if (annualBase <= 0) return null;

  return {
    annualBase,
    discretionaryAllowances: amountOf(
      block,
      ADVISOR_LINE_ITEM.DISCRETIONARY_ALLOWANCES
    ),
    discretionaryBonus: amountOf(block, ADVISOR_LINE_ITEM.DISCRETIONARY_BONUS),
    annualEquity: amountOf(block, ADVISOR_LINE_ITEM.ANNUAL_EQUITY),
    signOnBonus: amountOf(block, ADVISOR_LINE_ITEM.SIGN_ON),
  };
}

export interface IAdvisorRawOfferPackage {
  annual_base: number;
  discretionary_allowances?: number;
  discretionary_bonus?: number;
  annual_equity?: number;
  sign_on_bonus?: number;
}

export function readVariantPackageValues(
  pkg: IAdvisorRawOfferPackage
): IAdvisorPackageValues | null {
  if (!pkg.annual_base || pkg.annual_base <= 0) return null;

  return {
    annualBase: pkg.annual_base,
    discretionaryAllowances: pkg.discretionary_allowances || 0,
    discretionaryBonus: pkg.discretionary_bonus || 0,
    annualEquity: pkg.annual_equity || 0,
    signOnBonus: pkg.sign_on_bonus || 0,
  };
}

export function readVariantPackagesFromMeta(
  meta: Record<string, unknown> | undefined
): Record<string, IAdvisorRawOfferPackage> | null {
  const packages = meta?.["variant_packages"];
  if (!packages || typeof packages !== "object") return null;
  return packages as Record<string, IAdvisorRawOfferPackage>;
}

/** `is_user_modified` keeps a later auto-populate from overwriting what the advisor put here. */
function advisorRow(name: string, value: number): OfferModellerAddedField {
  return {
    name,
    value,
    is_current: false,
    is_percentage: false,
    percentage: null,
    original_value: value,
    is_basic_pay: false,
    auto_populate: false,
    is_user_modified: true,
  };
}

/** Existing rows kept as-is; the advisor's own row is replaced rather than stacked. */
function withAdvisorRow(
  rows: OfferModellerAddedField[] | undefined,
  name: string,
  amount: number
): OfferModellerAddedField[] {
  const kept = (rows ?? []).filter(
    row => row?.is_current || row?.name !== name
  );
  return amount > 0 ? [...kept, advisorRow(name, amount)] : kept;
}

export function useApplyAdvisorPackage() {
  const dispatch = useTypedDispatch();
  const store = useStore<RootState>();
  const [triggerAutoPopulate] = useLazyFetchAutoPopulateDataQuery();
  const { offer } = useTypedSelector(state => state.offers);
  const { company } = useTypedSelector(state => state.auth?.user!);

  return async (values: IAdvisorPackageValues) => {
    const offerId = offer?.id;
    const companyId = company?.id;

    // Base first: auto-populate values its percentage rows against whatever base is in state
    // when the response lands.
    dispatch(
      handleOfferModeller({
        key: "offer_fixed_cash",
        data: { proposed_annual_base: values.annualBase },
      })
    );

    if (offerId !== undefined && companyId !== undefined) {
      try {
        // `manual` makes the reducer apply the full payload, not just stock data.
        await triggerAutoPopulate({
          company_id: companyId,
          offer_id: offerId,
          manual: true,
        }).unwrap();
      } catch {
        // Keep going: the base and discretionary rows still land.
      }
    }

    // Through the store, not the hook's snapshot: auto-populate has just replaced these rows and
    // the values captured at render are a step behind.
    const modeller = store.getState().offers.offerModeller;

    dispatch(
      handleOfferModeller({
        key: "offer_fixed_cash",
        data: {
          proposed_annual_base: values.annualBase,
          allowances: withAdvisorRow(
            modeller.offer_fixed_cash?.allowances,
            ADVISOR_LINE_ITEM.DISCRETIONARY_ALLOWANCES,
            values.discretionaryAllowances
          ),
        },
      })
    );

    // proposed_bonus_target is left alone: it's a separate addend to the STI total, so setting it
    // on top of the configured rows would count the bonus twice.
    dispatch(
      handleOfferModeller({
        key: "offer_sti",
        data: {
          bonuses: withAdvisorRow(
            modeller.offer_sti?.bonuses,
            ADVISOR_LINE_ITEM.DISCRETIONARY_BONUS,
            values.discretionaryBonus
          ),
        },
      })
    );

    // Dispatched even with nothing to set: these branches own total direct compensation and total
    // remuneration, which the new base and bonus have just invalidated.
    dispatch(
      handleOfferModeller({
        key: "offer_lti",
        data:
          values.annualEquity > 0
            ? { proposed_annual_grant: values.annualEquity }
            : {},
      })
    );

    dispatch(handleOfferModeller({ key: "offer_benefits", data: {} }));

    if (values.signOnBonus > 0) {
      dispatch(
        handleOfferModeller({
          key: "offer_sign_on_bonus",
          data: {
            bonuses: withAdvisorRow(
              modeller.offer_sign_on_bonus?.bonuses,
              ADVISOR_LINE_ITEM.SIGN_ON,
              values.signOnBonus
            ),
          },
        })
      );
    }
  };
}
